import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { MessageModule } from 'primeng/message';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

// Composants
import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';

// Modèles
import { Client } from 'src/app/@shared/models/client/client.model';
import { ClientProfileMfa } from 'src/app/@shared/models/authorization/client-profile-mfa.model';
import { CustomProfile } from 'src/app/@shared/models/authorization/custom-profile.model';
import { Profile } from 'src/app/@shared/models/authorization/profile.model';

// Enums
import { MfaPolicyEnum } from 'src/app/@shared/enums/mfa-policy.enum';

export interface StandardProfileItem {
    profile: Profile;
    entry: ClientProfileMfa | undefined;
    requiresMfa: boolean;
}

@Component({
    selector: 'app-client-mfa-form',
    imports: [
        FormsModule,
        MessageModule,
        SelectButtonModule,
        TagModule,
        ToggleSwitchModule,
    ],
    templateUrl: './client-mfa-form.component.html',
    styleUrls: ['./client-mfa-form.component.scss'],
})
export class ClientMfaFormComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    public MfaPolicy = MfaPolicyEnum;

    public policyOptions = [
        { label: 'Facultatif', code: MfaPolicyEnum.Optional },
        { label: 'Selon les profils', code: MfaPolicyEnum.Partial },
        { label: 'Obligatoire pour tous', code: MfaPolicyEnum.All },
    ];

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _client!: Client;
    public get client(): Client {
        return this._client;
    }
    public set client(client: Client) {
        this._client = client;
        if (!!client) {
            this._loadData();
        }
    }

    public get selectedPolicyCode(): string {
        return this._client?.mfaPolicy?.code;
    }

    public set selectedPolicyCode(code: string) {
        if (this._client) {
            this._client.mfaPolicy = { code };
        }
    }

    private _standardProfileItems: StandardProfileItem[] = [];
    public get standardProfileItems(): StandardProfileItem[] {
        return this._standardProfileItems;
    }

    private _customProfiles: CustomProfile[] = [];
    public get customProfiles(): CustomProfile[] {
        return this._customProfiles;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public onPolicyChange(): void {
        this.clientService.save(this.client, { showMessages: false });
        if (this.client.mfaPolicy?.code === MfaPolicyEnum.Partial) {
            this._reloadEntries();
        }
    }

    public onToggleStandardProfile(item: StandardProfileItem, requiresMfa: boolean): void {
        if (requiresMfa) {
            const newEntry = ClientProfileMfa.init() as ClientProfileMfa;
            newEntry.profile = item.profile;
            newEntry.requiresMfa = true;
            this.clientProfileMfaService.save$(newEntry, { showMessages: false }).subscribe((created: ClientProfileMfa) => {
                item.entry = created;
                item.requiresMfa = true;
            });
        } else {
            if (item.entry) {
                const entryToDelete = item.entry;
                entryToDelete.deleted = true;
                this.clientProfileMfaService.save$(entryToDelete, { showMessages: false }).subscribe(() => {
                    item.entry = undefined;
                    item.requiresMfa = false;
                });
            }
        }
    }

    public onToggleCustomProfile(profile: CustomProfile, requiresMfa: boolean): void {
        profile.requiresMfa = requiresMfa;
        this.customProfileService.save(profile, { showMessages: false });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override subscribeToObservables(): void {
        this.subscriptions.add(
            this.clientService.selected$.subscribe((client: Client) => this.client = client)
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _loadData(): void {
        const isPartial = this._client?.mfaPolicy?.code === MfaPolicyEnum.Partial;

        forkJoin({
            profiles: this.profileService.getList$(),
            customProfiles: this.customProfileService.getList$(),
            entries: isPartial ? this.clientProfileMfaService.getList$({ expands: ['profile'] }) : of([]),
        }).subscribe(({ profiles, customProfiles, entries }) => {
            this._customProfiles = customProfiles as CustomProfile[];
            this._standardProfileItems = (profiles as Profile[]).map(profile => {
                const entry = (entries as ClientProfileMfa[]).find(e => e.profile?.code === profile.code);
                return {
                    profile,
                    entry,
                    requiresMfa: entry?.requiresMfa ?? false,
                };
            });
        });
    }

    private _reloadEntries(): void {
        this.clientProfileMfaService.getList$({ expands: ['profile'] }).subscribe((entries: ClientProfileMfa[]) => {
            this._standardProfileItems = this._standardProfileItems.map(item => {
                const entry = entries.find(e => e.profile?.code === item.profile.code);
                return {
                    ...item,
                    entry,
                    requiresMfa: entry?.requiresMfa ?? false,
                };
            });
        });
    }
}
